import React, { FC, useMemo, useState } from 'react';
import './DataCircadian.scss';
import { CircularProgress, Stack, Typography } from '@mui/material';
import { useEnergyTrend } from '../../../../hooks';
import { PeakDipsValuesCard } from '../../DataCircadianDashboard';
import EnergyDurationCard from './EnergyDurationCard';
import { PeakEnergyCard } from './PeakEnergyCard';
import { useCircadianAnalysis } from '../../../../data-access';
import { CustomTooltip, CustomTooltipContent } from '../../../common';
import { HelpOutline } from '@mui/icons-material';
import { DataViewMenuItems } from '../../constants';
import { EnergyDataTrend } from '@demind-inc/core';
import { ViewAllContainer } from '../ViewAllContainer';
import { DataCircadianFullView } from './DataCircadianFullView';
import { ViewAllTitle } from '../../types';
import { useTranslation } from 'react-i18next';

const DataCircadian: FC = () => {
  const { t } = useTranslation();
  const [viewAllTitle, setViewAllTitle] = useState<ViewAllTitle | null>(null);
  const {
    energyDailyTrend,
    energyWeeklyTrend,
    peakEnergyDiff,
    peakEnergyDiffColor,
    avgPeakEnergyDiff,
    avgPeakEnergyDiffColor,
    dataTrendPeriod,
    highEnergyDurationDiff,
    highEnergyDurationDiffColor,
    weeklyHighEnergyDuration,
    weeklyHighEnergyDurationDiff,
    weeklyHighEnergyDurationDiffColor,
    isLoading: energyLoading,
  } = useEnergyTrend();
  const { selectedDatePeaksDipsBoundaries, avgPeaksDipsBounrdaies, isFetchingSelectedCircadian } =
    useCircadianAnalysis();
  const explanation = DataViewMenuItems.find((item) => item.id === 'dataCircadian')?.explanation;
  const localizedExplanation = explanation
    ? {
        ...explanation,
        title: t('data.circadian.tooltipTitle'),
        description: t('data.circadian.tooltipDescription'),
      }
    : undefined;
  const isWeekly = dataTrendPeriod === 'weekly';
  const chartData = useMemo(() => {
    return ((energyWeeklyTrend?.data ?? []) as EnergyDataTrend[]).map(
      (item) => item.peakEnergy ?? 0
    );
  }, [energyWeeklyTrend]);

  const isLoading = energyLoading || isFetchingSelectedCircadian;
  return (
    <Stack className="data-circadian">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body1" className="data-circadian__title">
          {t('data.circadian.title')}
          <CustomTooltip title={<CustomTooltipContent {...localizedExplanation} />}>
            <HelpOutline fontSize="inherit" width={6} className="data-circadian__title__icon" />
          </CustomTooltip>
        </Typography>
        <Typography
          variant="subtitle2"
          className="data-satisfaction__view-all"
          onClick={() => setViewAllTitle(t('data.circadian.title'))}
        >
          {t('data.common.viewAll')}
        </Typography>
      </Stack>

      <ViewAllContainer title={viewAllTitle} onClose={() => setViewAllTitle(null)}>
        <DataCircadianFullView
          peakEnergy={
            !isWeekly
              ? (energyDailyTrend?.peakEnergy ?? 0)
              : (energyWeeklyTrend?.avgPeakEnergy ?? 0)
          }
          dataTrendPeriod={dataTrendPeriod}
          diff={isWeekly ? avgPeakEnergyDiff : peakEnergyDiff}
          diffColor={isWeekly ? avgPeakEnergyDiffColor : peakEnergyDiffColor}
          energyDailyTrend={energyDailyTrend}
          highEnergyDurationDiff={highEnergyDurationDiff}
          highEnergyDurationDiffColor={highEnergyDurationDiffColor}
          weeklyHighEnergyDuration={weeklyHighEnergyDuration}
          weeklyHighEnergyDurationDiff={weeklyHighEnergyDurationDiff}
          weeklyHighEnergyDurationDiffColor={weeklyHighEnergyDurationDiffColor}
          targetPeakDipStartEndSet={selectedDatePeaksDipsBoundaries}
          basePeakDipStartEndSet={avgPeaksDipsBounrdaies}
          isWeekly={isWeekly}
          chartData={chartData}
        />
      </ViewAllContainer>

      <PeakEnergyCard
        peakEnergy={
          !isWeekly ? (energyDailyTrend?.peakEnergy ?? 0) : (energyWeeklyTrend?.avgPeakEnergy ?? 0)
        }
        dataTrendPeriod={dataTrendPeriod}
        diff={isWeekly ? avgPeakEnergyDiff : peakEnergyDiff}
        diffColor={isWeekly ? avgPeakEnergyDiffColor : peakEnergyDiffColor}
      />
      <Stack className="data-circadian__content-item" mt={2} flex={1}>
        {isLoading ? (
          <Stack justifyContent="center" alignItems="center" flex={1}>
            <CircularProgress size={32} />
          </Stack>
        ) : (
          <>
            <EnergyDurationCard
              energyDailyTrend={energyDailyTrend}
              highEnergyDurationDiff={highEnergyDurationDiff}
              highEnergyDurationDiffColor={highEnergyDurationDiffColor}
              weeklyHighEnergyDuration={weeklyHighEnergyDuration}
              weeklyHighEnergyDurationDiff={weeklyHighEnergyDurationDiff}
              weeklyHighEnergyDurationDiffColor={weeklyHighEnergyDurationDiffColor}
              isSimplified={true}
            />

            <PeakDipsValuesCard
              targetPeakDipStartEndSet={selectedDatePeaksDipsBoundaries}
              basePeakDipStartEndSet={avgPeaksDipsBounrdaies}
              isWeekly={isWeekly}
              isSimplified
            />
          </>
        )}
      </Stack>
    </Stack>
  );
};

export default DataCircadian;
